import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ComprehensiveScoring } from "@/components/analytics/ComprehensiveScoring";
import { ViralityMetrics } from "@/components/content/ViralityMetrics";
import { SystemOptimization } from "@/components/monitoring/SystemOptimization";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useAgentMetrics } from "@/hooks/useAgentMetrics";

export const MetricsTab = () => {
  const { agentMetrics } = useAgentMetrics();
  
  const { data: latestContent, isLoading: isLoadingContent, error: contentError } = useQuery({
    queryKey: ['latest-content'],
    queryFn: async () => {
      console.log('Fetching latest content...');
      try {
        const { data, error } = await supabase
          .from('generated_content')
          .select('id')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        console.log('Latest content query result:', { data, error });

        if (error) {
          console.error('Supabase query error:', error);
          throw error;
        }

        return data;
      } catch (error) {
        console.error('Error in latest content query:', error);
        throw error;
      }
    },
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 30000 // Consider data fresh for 30 seconds
  });

  console.log('MetricsTab render state:', {
    latestContent,
    isLoadingContent,
    contentError,
    agentMetrics
  });

  if (isLoadingContent) {
    console.log('Rendering loading state...');
    return (
      <div className="space-y-4">
        <Skeleton className="h-[200px] w-full" />
        <Skeleton className="h-[200px] w-full" />
        <Skeleton className="h-[200px] w-full" />
      </div>
    );
  }

  // If there's no content yet, show a more friendly message
  if (!latestContent && !contentError) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No Content Available</AlertTitle>
        <AlertDescription>
          Generate some content first to see metrics and analytics.
        </AlertDescription>
      </Alert>
    );
  }

  if (contentError) {
    console.error('Rendering error state:', contentError);
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error Loading Metrics</AlertTitle>
        <AlertDescription>
          Unable to load metrics data. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Tabs defaultValue="agent-metrics" className="space-y-4">
      <TabsList>
        <TabsTrigger value="agent-metrics">Agent Metrics</TabsTrigger>
        <TabsTrigger value="analytics-overview">Analytics Overview</TabsTrigger>
      </TabsList>

      <TabsContent value="agent-metrics">
        {agentMetrics?.map((metric) => {
          console.log('Rendering agent metric:', metric);
          const performanceMetrics = metric.performance_metrics as { score?: number; status?: string };
          return (
            <Card key={metric.id} className="bg-white dark:bg-gray-800 hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      Agent ID: {metric.agent_id}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Performance Score: {performanceMetrics?.score || "N/A"}
                    </p>
                  </div>
                  <Badge
                    variant={performanceMetrics?.status === 'active' ? 'default' : 'secondary'}
                    className={
                      performanceMetrics?.status === 'active' 
                        ? 'bg-green-500 hover:bg-green-600' 
                        : 'bg-gray-500 hover:bg-gray-600'
                    }
                  >
                    {performanceMetrics?.status || "Unknown"}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </TabsContent>

      <TabsContent value="analytics-overview" className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card className="col-span-1">
            <ComprehensiveScoring contentId={latestContent?.id} />
          </Card>
          
          <Card className="col-span-1">
            <ViralityMetrics contentId={latestContent?.id} />
          </Card>
          
          <Card className="col-span-1">
            <SystemOptimization />
          </Card>
        </div>
      </TabsContent>
    </Tabs>
  );
};